// Sugalarm — Cycle 387: numbers spoken as WORDS, in the SPA's voice.
//
// The TypeScript twin of `voice_service.spell_number` on the server. Same
// contract, same output, deliberately: the emergency CALL and the in-app /
// kiosk voice are the same household hearing the same value, so
// "seventy-eight" must not become "78" when the sentence happens to be
// composed on the client. The two spellers are pinned to each other by
// shared golden vectors (`__tests__/spellNumber.test.ts` mirrors
// `server/tests/test_voice_spoken_number_and_signoff.py`).
//
// WHY AT ALL: ElevenLabs — the live voice on both surfaces — mis-reads a
// bare numeral, and a glucose value is the one thing a listener has to
// catch. Spelling it out removes the engine's discretion. It is also
// engine-agnostic (every TTS reads the word form correctly), so this holds
// through the `edge_tts` and Web Speech fallback tiers too.
//
// SPOKEN SURFACES ONLY. Anything a user READS keeps its digits — a number
// you read is faster and less ambiguous as a numeral; a number you hear is
// the opposite. Never import this into a component that renders text.

const ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen",
] as const;

const TENS: Record<number, string> = {
  2: "twenty", 3: "thirty", 4: "forty", 5: "fifty",
  6: "sixty", 7: "seventy", 8: "eighty", 9: "ninety",
};

/** English words for an integer 0–999, or null when out of range. */
function spellInteger(n: number): string | null {
  if (!Number.isInteger(n) || n < 0 || n > 999) return null;
  if (n < 20) return ONES[n]!;
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    return ones === 0 ? TENS[tens]! : `${TENS[tens]}-${ONES[ones]}`;
  }
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const head = `${ONES[hundreds]} hundred`;
  return rest === 0 ? head : `${head} ${spellInteger(rest)}`;
}

/** A numeral written in DIGITS, respelled as WORDS.
 *
 *  `"78"` → `"seventy-eight"`, `"4.3"` → `"four point three"`.
 *
 *  FALLS BACK TO THE INPUT VERBATIM on anything it cannot spell (negative,
 *  past 999, malformed). That direction is the safe one — the fallback is
 *  exactly the pre-387 behaviour, so the worst case is a sentence that
 *  sounds like it did yesterday, never a throw and never silence. */
export function spellNumber(numeral: string): string {
  const dot = numeral.indexOf(".");
  const whole = dot === -1 ? numeral : numeral.slice(0, dot);
  const frac = dot === -1 ? "" : numeral.slice(dot + 1);
  const digits = /^[0-9]+$/;
  // A separator with no digits behind it ("5.") is malformed, not a
  // shorthand — fall back rather than quietly normalising it away.
  if (!digits.test(whole)) return numeral;
  if (dot !== -1 && !digits.test(frac)) return numeral;
  const words = spellInteger(Number(whole));
  if (words == null) return numeral;
  if (!frac) return words;
  const spokenFrac = [...frac].map((d) => ONES[Number(d)]!).join(" ");
  return `${words} point ${spokenFrac}`;
}

/** Convenience for a numeric value: rounds to `decimals` places first, so
 *  a float never reaches the speller as "2.4000000000000004". */
export function spellValue(n: number, decimals = 0): string {
  if (!Number.isFinite(n)) return String(n);
  const fixed = n.toFixed(decimals);
  // toFixed keeps a leading "-" and can emit "-0"; the speller declines
  // negatives, so this falls back verbatim rather than mis-speaking a sign.
  return spellNumber(fixed);
}
